import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  onBackButtonClick,
  onChatEnded,
  onChatEstablished,
  onChatReady,
  onMaximize,
  onMessageSent,
  onMinimize,
  onSessionContinuation,
} from '../../utils/chat/chat-event-handler';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { eventBus } from '../../utils/event-bus';
import { useCustomTracking } from './useTracking';
import { CaseDataContext } from '../../contexts/CaseDataContext';
import { useNRLogging } from './useNRLogging';
import { useMIAWApi } from '../miaw/useMIAWApi';
import { isOrderHelpPage, isStudioPage } from '../../utils/chat/page-utils';
import { getConversationId } from '../../utils/miaw/chat-config';
import { MiawChatContext } from '../../contexts/MiawChatContext';

const useCustomChatEvents = () => {
  const {
    state: { caseData },
  } = useContext(CaseDataContext);

  const { setState: setMiawState } = useContext(MiawChatContext);

  const { sendTrackingEvent } = useCustomTracking();
  const { logChat } = useNRLogging();
  const { syncCustomerUrlToChat, updateOrderHelpCaseDescription } = useMIAWApi();
  const {
    chatState: { gcpChatData, chatWindowHandler },
    setChatState,
  } = useContext(CAContext);

  const { setState: setChatbotState } = useContext(ChatbotContext);

  const [orderHelpPageAttributes, setOrderHelpPageAttributes] = useState({
    description: undefined,
    orderNumber: undefined,
  });

  const orderHelpPageAttributesRef = useRef(orderHelpPageAttributes);

  useEffect(() => {
    orderHelpPageAttributesRef.current = orderHelpPageAttributes;
  }, [orderHelpPageAttributes]);

  const initiateOHChat = async (data) => {
    console.log('initiateOHChat', { data });

    setOrderHelpPageAttributes({
      description: data?.description,
      orderNumber: data?.orderNumber,
    });

    if (data?.orderNumber) {
      setChatbotState((prev) => ({
        ...prev,
        orderNumber: data.orderNumber,
      }));
    }

    eventBus.dispatch('onCAButtonClicked', data);
  };

  const ohOpenChatHandler = (data) => {
    console.log('Inside the OH Chat handler');
    initiateOHChat(data);
  };

  const [studioDesignLink, setStudioDesignLink] = useState<string>('');

  const populateLiveDesignLink = (conversationId?: string, invokeEvent?: boolean) => {
    if (typeof window === 'undefined') return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window?.addEventListener('getLiveDesignURLLink', (e: any) => {
      console.info('Studio getLiveDesignURLLink', e);
      if (e?.detail) {
        const designLink = `https://${e.detail}`;
        setStudioDesignLink(designLink);
        const currentPath = window?.location?.origin + window?.location?.pathname;
        if (conversationId) syncCustomerUrlToChat(conversationId, designLink, currentPath);
      }
    });
    if (invokeEvent) {
      window?.dispatchEvent(new CustomEvent('generateLiveDesignLinkEvent'));
    }
  };

  const onChatMessageHandler = (event: Event) => {
    console.log('Custom chat message event triggered: onChatMessage', event);
    if (caseData) {
      sendTrackingEvent('Chat Initiated', 'Chat submitted - Chat Anywhere', `${caseData?.pagePath};Chat Submit`, 'Global Chat');
    }
    onMessageSent();
  };

  const onChatEndHandler = () => {
    console.log('Custom chat event triggered: onChatEnd');
    if (caseData) {
      sendTrackingEvent('Chat Initiated', 'Chat closed - Chat Anywhere', `${caseData?.pagePath};Chat Close`, 'Global Chat');
    }
    onChatEnded();
    removeEventHandlers();
    logChat('CA_CHAT_ENDED_BY_USER', {});
  };

  const onChatStartHandler = () => {
    console.log('Custom chat event triggered: onChatStart');
    if (caseData) {
      sendTrackingEvent('Chat Initiated', 'Chat opened - Chat Anywhere', `${caseData?.pagePath};Chat Start`, 'Global Chat');
    }

    // Wait for 5 seconds to ensure the chat is fully established
    setTimeout(() => {
      const conversationId = getConversationId();
      console.log('Conversation ID:', conversationId);

      if (conversationId) {
        if (isOrderHelpPage()) updateOrderHelpCaseDescription(conversationId, orderHelpPageAttributesRef.current.description || '');
        if (isStudioPage()) {
          populateLiveDesignLink(conversationId, true);
        }
      }
    }, 5000);

    onChatEstablished();
    logChat('CA_CHAT_ESTABLISHED', {});
  };

  useEffect(() => {
    if (studioDesignLink) {
      setMiawState((prev) => ({
        ...prev,
        studioDesignLink,
      }));
    }
  }, [studioDesignLink]);

  const handleAddEventListeners = useCallback(() => {
    console.log('Adding custom chat event listeners');

    // Define event handlers with stable references
    const onMaximizeHandler = () => {
      console.log('Custom chat event triggered: onMaximize');
      onMaximize();
    };

    const onMinimizeHandler = () => {
      console.log('Custom chat event triggered: onMinimize');
      onMinimize();
    };

    const onSessionContinuationHandler = () => {
      console.log('Custom chat event triggered: onSessionContinuation');
      onSessionContinuation();
      const currentUrl = window.location.href;
      const currentPath = window.location.origin + window.location.pathname;
      const previousUrl = document.referrer;
      const conversationId = getConversationId();
      if (previousUrl !== currentUrl) {
        if (conversationId) {
          if (isStudioPage()) {
            populateLiveDesignLink(conversationId, true);
          } else {
            syncCustomerUrlToChat(conversationId, currentUrl, currentPath);
          }
        }
      }
    };

    const onChatReadyHandler = () => {
      console.log('Custom chat ready event triggered: onChatReady');
      /** Just to add a event listener so that if design link is available, it will be populated */
      populateLiveDesignLink(undefined, false);
      onChatReady(gcpChatData?.status?.isChatEnabled || false);
    };

    const onBackButtonClickedHandler = () => {
      console.log('Custom chat event triggered: onBackButtonClicked');
      onBackButtonClick();
    };

    // Add event listeners
    document.addEventListener('onMaximize', onMaximizeHandler);
    document.addEventListener('onMinimize', onMinimizeHandler);
    document.addEventListener('onChatEnd', onChatEndHandler);
    document.addEventListener('onChatStart', onChatStartHandler);
    document.addEventListener('onSessionContinuation', onSessionContinuationHandler);
    document.addEventListener('onChatMessage', onChatMessageHandler);
    document.addEventListener('onChatReady', onChatReadyHandler);
    document.addEventListener('onBackButtonClicked', onBackButtonClickedHandler);

    setChatState((prevState) => ({
      ...prevState,
      isChatEventListenersAdded: true,
    }));

    return () => {
      console.log('Removing custom chat event listeners');
      document.removeEventListener('onMaximize', onMaximizeHandler);
      document.removeEventListener('onMinimize', onMinimizeHandler);
      document.removeEventListener('onChatEnd', onChatEndHandler);
      document.removeEventListener('onChatStart', onChatStartHandler);
      document.removeEventListener('onSessionContinuation', onSessionContinuationHandler);
      document.removeEventListener('onChatMessage', onChatMessageHandler);
      document.removeEventListener('onChatReady', onChatReadyHandler);
      document.removeEventListener('onBackButtonClicked', onBackButtonClickedHandler);
    };
  }, [gcpChatData, caseData, studioDesignLink]);

  const addOpenChatEventListener = useCallback(
    (callback: () => void) => {
      console.log('Adding open chat event listeners');

      // Define event handlers with stable references
      const hpOpenChatHandler = () => {
        console.log('HPOpenChat event triggered');
        chatWindowHandler('show');
        setChatState((prevState) => ({
          ...prevState,
          chatWindow: 'maximized',
        }));

        window?.dispatchEvent(new CustomEvent('generateLiveDesignLinkEvent'));
        callback();
      };

      const onCAButtonClickedHandler = () => {
        console.log('onCAButtonClicked event triggered');
        callback();
      };

      const initCAfromExternalSourceHandler = () => {
        console.log('initCAfromExternalSource event triggered');
        callback();
      };

      /** For Help Panel */
      eventBus.on('HPOpenChat', hpOpenChatHandler);
      eventBus.on('onCAButtonClicked', onCAButtonClickedHandler);
      eventBus.on('initCAfromExternalSource', initCAfromExternalSourceHandler);
      eventBus.on('OHOpenChat', ohOpenChatHandler);

      // Return cleanup function
      return () => {
        console.log('Removing open chat event listeners');
        eventBus.remove('HPOpenChat', hpOpenChatHandler);
        eventBus.remove('onCAButtonClicked', onCAButtonClickedHandler);
        eventBus.remove('initCAfromExternalSource', initCAfromExternalSourceHandler);
        eventBus.remove('OHOpenChat', ohOpenChatHandler);
      };
    },
    [initiateOHChat],
  );
  const sendChatEvents = useCallback(
    (
      eventName: 'onMaximize' | 'onMinimize' | 'onChatEnd' | 'onChatStart' | 'onSessionContinuation' | 'onChatMessage' | 'onChatReady' | 'onBackButtonClicked',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any,
    ) => {
      const event = new CustomEvent(eventName, { detail: data });
      document.dispatchEvent(event);
      console.log(`Custom chat event ${eventName} sent with data:`, data);
    },
    [],
  );

  const removeEventHandlers = () => {
    document.addEventListener('onChatEnd', onChatEndHandler);
    document.addEventListener('onChatStart', onChatStartHandler);
    document.addEventListener('onChatMessage', onChatMessageHandler);
  };

  return { handleAddEventListeners, sendChatEvents, addOpenChatEventListener };
};

export { useCustomChatEvents };
